import type { Sport } from "@teamna/shared";
import { useId } from "react";
import { contrastRatio, markColor } from "../../lib/group-color.ts";

/**
 * The drawings on the Home greeting band (2026-10-14): a swimming pool for swimming, a client on a
 * reformer for Pilates, a padel court, a football pitch, a gymnastics ribbon and beam, and a week of
 * sessions for any other activity. A business with no sport keeps the white wave in `view.tsx`.
 *
 * Swimming and Pilates come from brandable vector artwork: shapes only, no image, no library. Every
 * colour in them is made from one base colour, the band colour, so a club with a logo colour gets its
 * own pool or studio (decision D7). The base is the logo colour made dark enough for white text
 * (`markColor`, AA 4.5:1), or Teamna blue. Only the Pilates skin is fixed.
 *
 * Padel, football, gymnastics and other come from the design artifact: white lines at about 30% on
 * the band, and one fixed colour, the yellow padel ball. They do not take the band colour.
 */

const TEAMNA_BLUE = "#326CC5";

/** The base colour of the band drawing: the logo colour, AA-safe for white text, or Teamna blue. */
export function bandBase(brandColor: string | null): string {
  if (!brandColor || !/^#[0-9a-fA-F]{6}$/.test(brandColor)) return TEAMNA_BLUE;
  return markColor(brandColor);
}

/** `hex` moved toward `to` by `amount` (0 to 1), as `#RRGGBB`. */
export function mix(hex: string, to: string, amount: number): string {
  const a = Number.parseInt(hex.slice(1), 16);
  const b = Number.parseInt(to.slice(1), 16);
  const parts = [16, 8, 0].map((shift) => Math.round(((a >> shift) & 255) * (1 - amount) + ((b >> shift) & 255) * amount));
  return `#${parts.map((part) => part.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

export type PilatesPalette = { primary: string; dark: string; soft: string; skin: string };

/** Top and leaves lighter than the band, leggings and hair darker, the machine pale. The skin stays. */
export function pilatesPalette(base: string): PilatesPalette {
  return {
    primary: mix(base, "#FFFFFF", 0.42),
    dark: mix(base, "#000000", 0.55),
    soft: mix(base, "#FFFFFF", 0.8),
    skin: "#E7C3A4",
  };
}

export type SwimPalette = {
  water: string;
  waterDeep: string;
  waterLight: string;
  marking: string;
  ropeLight: string;
  ropeDark: string;
  deck: string;
  grout: string;
  rim: string;
  highlight: string;
};

/** `hex` made darker until white text on it reaches `ratio`. */
function darkTo(hex: string, ratio: number): string {
  for (let amount = 0; amount < 1; amount += 0.02) {
    const candidate = mix(hex, "#000000", amount);
    if (contrastRatio(candidate, "#FFFFFF") >= ratio) return candidate;
  }
  return "#000000";
}

/** The lightest tint of `hex`, at most `amount` toward white, that keeps white text at AA (4.5:1). */
function lightestAA(hex: string, amount: number): string {
  for (let step = amount; step > 0; step -= 0.01) {
    const candidate = mix(hex, "#FFFFFF", step);
    if (contrastRatio(candidate, "#FFFFFF") >= 4.5) return candidate;
  }
  return hex;
}

/**
 * The pool colours. Every colour that can sit under the text keeps white text at AA (4.5:1): the
 * water, the floor lines, both float colours and the light on the water. Luminance is convex in the
 * colour channels, so a blend of these colours (opacity, the mask, the gradient) is never lighter
 * than the lightest of them: the text stays at AA on every pixel. The water starts a little darker
 * than the band (6:1), so the light floats still stand out. Only the tiled edge is pale, and the
 * band keeps the text away from it (`view.tsx`).
 */
export function swimPalette(base: string): SwimPalette {
  const water = darkTo(base, 6);
  return {
    water,
    waterDeep: mix(water, "#000000", 0.3),
    waterLight: lightestAA(water, 0.23),
    marking: mix(water, "#000000", 0.5),
    ropeLight: lightestAA(water, 0.66),
    ropeDark: mix(water, "#000000", 0.35),
    deck: mix(base, "#FFFFFF", 0.92),
    grout: mix(base, "#FFFFFF", 0.79),
    rim: mix(base, "#FFFFFF", 0.71),
    highlight: lightestAA(water, 1),
  };
}

/** The colours of the pool that can sit under the text (all except the tiled edge). */
export const SWIM_UNDER_TEXT = ["water", "waterDeep", "waterLight", "marking", "ropeLight", "ropeDark", "highlight"] as const;

/** The sports with their own drawing: all of them. Null (no sport, or an unknown one): the white wave. */
export function bandArtFor(sport: Sport | null | undefined): Sport | null {
  return sport === "swimming" ||
    sport === "pilates" ||
    sport === "padel" ||
    sport === "football" ||
    sport === "gymnastics" ||
    sport === "other"
    ? sport
    : null;
}

export function BandArt({ sport, brandColor }: { sport: Sport; brandColor: string | null }) {
  const base = bandBase(brandColor);
  switch (sport) {
    case "swimming":
      return <SwimLanesArt base={base} />;
    case "pilates":
      return <PilatesArt base={base} />;
    case "padel":
      return <PadelArt />;
    case "football":
      return <FootballArt />;
    case "gymnastics":
      return <GymnasticsArt />;
    case "other":
      return <WeekArt />;
  }
}

/**
 * How much room the text leaves on the right of a band of 768px and wider. Static class names, so
 * Tailwind finds them.
 *
 * Pilates and the four line drawings have a fixed size there, so the room keeps all of the drawing
 * clear of the text. The pool is different: it fills the band height, so it can sit under the text by
 * design (every pool colour under the text is AA, see `swimPalette`), and its room only keeps the
 * text off the pale tiled edge.
 */
export const TEXT_ROOM: Record<Sport, string> = {
  swimming: "@3xl:mr-[68px]",
  pilates: "@3xl:mr-[340px]",
  padel: "@3xl:mr-[350px]",
  football: "@3xl:mr-[235px]",
  gymnastics: "@3xl:mr-[390px]",
  other: "@3xl:mr-[280px]",
};

/** The band behind the pool: the deep water, so a wide band is all pool. AA for white text. */
export function swimBandBackground(brandColor: string | null): string {
  return swimPalette(bandBase(brandColor)).waterDeep;
}

/**
 * The pool from above: lanes, lane ropes, the floor lines with their T, light on the water, and the
 * tiled edge on the right. It keeps its 3:1 shape on the right of the band, so on a wide screen the
 * lanes do not run under the text; its left side fades into the deep water of the band
 * (`swimBandBackground`). The lanes are quiet on the left too, and everywhere on a band narrower than
 * 768px (the band is a CSS container), where the text covers most of the pool. On such a band most of
 * the tiled edge sits outside the band, so what shows of it stays inside the band padding, away from
 * the text.
 */
export function SwimLanesArt({ base }: { base: string }) {
  const c = swimPalette(base);
  // Each band on a page needs its own ids for the gradients, the patterns and the mask.
  const id = `swim-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      data-art="swimming"
      viewBox="0 0 1800 600"
      preserveAspectRatio="xMaxYMid meet"
      fill="none"
      className="pointer-events-none absolute inset-y-0 right-0 -z-10 h-full w-auto max-w-none [mask-image:linear-gradient(90deg,transparent,#000_20%)] @max-3xl:translate-x-[7%]"
    >
      <defs>
        <linearGradient id={`${id}-water-surface`} x1="0" y1="0" x2="1634" y2="600" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={c.waterDeep} />
          <stop offset="0.55" stopColor={c.water} />
          <stop offset="1" stopColor={c.waterLight} />
        </linearGradient>
        <linearGradient id={`${id}-copy-fade`} x1="0" y1="0" x2="1300" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="white" stopOpacity="0.08" />
          <stop offset="0.4" stopColor="white" stopOpacity="0.08" />
          <stop offset="0.67" stopColor="white" stopOpacity="0.35" />
          <stop offset="1" stopColor="white" stopOpacity="1" />
        </linearGradient>
        <mask id={`${id}-quiet-left`} maskUnits="userSpaceOnUse" x="0" y="0" width="1634" height="600" style={{ maskType: "alpha" }}>
          <rect width="1634" height="600" fill={`url(#${id}-copy-fade)`} />
        </mask>
        <clipPath id={`${id}-water-clip`}>
          <rect width="1634" height="600" />
        </clipPath>
        <pattern id={`${id}-lane-floats`} width="34" height="18" patternUnits="userSpaceOnUse">
          <rect x="1" y="3" width="13" height="12" rx="4" fill={c.ropeDark} />
          <rect x="18" y="3" width="13" height="12" rx="4" fill={c.ropeLight} />
          <path d="M4 6 H11 M21 6 H28" stroke={c.highlight} strokeOpacity="0.2" strokeWidth="1.2" strokeLinecap="round" fill="none" />
        </pattern>
        <pattern id={`${id}-deck-grid`} width="56" height="56" patternUnits="userSpaceOnUse" x="1656" y="-8">
          <path d="M0 56 V0 H56" fill="none" stroke={c.grout} strokeWidth="2" />
        </pattern>
      </defs>
      <g data-layer="water">
        <rect width="1800" height="600" fill={c.deck} />
        <rect width="1634" height="600" fill={`url(#${id}-water-surface)`} />
      </g>
      <g data-layer="pool-details" clipPath={`url(#${id}-water-clip)`} mask={`url(#${id}-quiet-left)`} className="@max-3xl:opacity-35">
        <g data-layer="water-reflections" fill="none" strokeLinecap="round">
          <path d="M800 -24 C722 70 854 119 1033 124 S1250 222 1158 286" stroke={c.highlight} strokeWidth="3.5" opacity="0.13" />
          <path d="M829 -24 C751 78 872 104 1045 108 S1274 216 1190 285" stroke={c.highlight} strokeWidth="1.5" opacity="0.17" />
          <path d="M1340 -50 C1230 46 1360 120 1516 116 S1710 194 1600 286" stroke={c.highlight} strokeWidth="4" opacity="0.16" />
          <path d="M1380 -40 C1273 59 1380 91 1511 94 S1730 185 1620 258" stroke={c.highlight} strokeWidth="1.5" opacity="0.13" />
          <path d="M1143 184 C1027 165 955 229 1002 305 S1200 342 1286 390 S1390 516 1298 624" stroke={c.highlight} strokeWidth="3" opacity="0.14" />
          <path d="M1126 204 C1044 199 993 239 1032 294 S1211 316 1307 371 S1422 520 1320 625" stroke={c.highlight} strokeWidth="1.5" opacity="0.14" />
          <path d="M760 622 C742 540 824 494 919 506 S1087 469 1078 427" stroke={c.highlight} strokeWidth="2.5" opacity="0.12" />
          <path d="M1449 624 C1404 550 1448 475 1551 449 S1682 367 1654 315" stroke={c.highlight} strokeWidth="3" opacity="0.16" />
          <path d="M1466 623 C1426 552 1472 492 1568 468 S1701 382 1679 319" stroke={c.highlight} strokeWidth="1.5" opacity="0.12" />
        </g>
        <g data-layer="lane-markings" stroke={c.marking} fill="none" strokeLinecap="butt" opacity="0.48">
          <path d="M-20 75 H1510" strokeWidth="11" />
          <path d="M1510 46 V104" strokeWidth="16" />
          <path d="M-20 225 H1510" strokeWidth="11" />
          <path d="M1510 196 V254" strokeWidth="16" />
          <path d="M-20 375 H1510" strokeWidth="11" />
          <path d="M1510 346 V404" strokeWidth="16" />
          <path d="M-20 525 H1510" strokeWidth="11" />
          <path d="M1510 496 V554" strokeWidth="16" />
        </g>
        <g data-layer="lane-ropes">
          <g data-lane-boundary="1" transform="translate(0 141)">
            <path d="M-2 10 H1628" stroke={c.marking} strokeWidth="2" opacity="0.5" />
            <rect x="0" y="0" width="1580" height="18" fill={`url(#${id}-lane-floats)`} />
            <path d="M1580 9 H1621" stroke={c.ropeLight} strokeWidth="2.5" />
            <circle cx="1618" cy="9" r="5" fill="none" stroke={c.ropeLight} strokeWidth="2.5" />
          </g>
          <g data-lane-boundary="2" transform="translate(0 291)">
            <path d="M-2 10 H1628" stroke={c.marking} strokeWidth="2" opacity="0.5" />
            <rect x="0" y="0" width="1580" height="18" fill={`url(#${id}-lane-floats)`} />
            <path d="M1580 9 H1621" stroke={c.ropeLight} strokeWidth="2.5" />
            <circle cx="1618" cy="9" r="5" fill="none" stroke={c.ropeLight} strokeWidth="2.5" />
          </g>
          <g data-lane-boundary="3" transform="translate(0 441)">
            <path d="M-2 10 H1628" stroke={c.marking} strokeWidth="2" opacity="0.5" />
            <rect x="0" y="0" width="1580" height="18" fill={`url(#${id}-lane-floats)`} />
            <path d="M1580 9 H1621" stroke={c.ropeLight} strokeWidth="2.5" />
            <circle cx="1618" cy="9" r="5" fill="none" stroke={c.ropeLight} strokeWidth="2.5" />
          </g>
        </g>
      </g>
      <g data-layer="pool-edge">
        <rect x="1623" width="11" height="600" fill={c.marking} opacity="0.26" />
        <rect x="1634" width="22" height="600" fill={c.rim} />
        <path d="M1638 0 V600" stroke={c.highlight} strokeOpacity="0.7" strokeWidth="2" />
        <path d="M1649 0 V600" stroke={c.grout} strokeWidth="1" />
        <rect x="1656" width="144" height="600" fill={c.deck} />
        <rect x="1656" width="144" height="600" fill={`url(#${id}-deck-grid)`} />
      </g>
    </svg>
  );
}

/**
 * A client on the reformer holding a strap, one arm over her head, with an arch and leaves behind
 * her. On a band of 768px and wider: 200px high on the right, and the text stops before it
 * (`view.tsx`). On a narrower band it sits behind the text, so it turns into a dark shade (black at
 * 20%): darker than the band, so white text only gains contrast.
 */
export function PilatesArt({ base }: { base: string }) {
  const c = pilatesPalette(base);
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      data-art="pilates"
      viewBox="540 0 660 400"
      preserveAspectRatio="xMaxYMax meet"
      className="pointer-events-none absolute right-2 bottom-0 -z-10 h-[200px] w-auto max-w-none @max-3xl:-right-24 @max-3xl:h-[60%] @max-3xl:brightness-0 @max-3xl:opacity-20"
    >
      <g data-layer="studio-decoration">
        <path data-part="arch" fill={c.soft} opacity="0.16" d="M931 311V110C931 60.3 971.3 20 1021 20S1111 60.3 1111 110V311Z" />
        <g data-part="botanical" fill={c.primary} opacity="0.75">
          <g fill="none" stroke={c.primary} strokeWidth="2.5" strokeLinecap="round" opacity="0.7">
            <path d="M1113 308C1119 255 1110 213 1107 173" />
            <path d="M1114 296C1115 236 1121 185 1129 146" />
            <path d="M1115 285C1120 251 1125 228 1132 211" />
            <path d="M1114 287C1110 267 1107 249 1098 234" />
          </g>
          <path opacity="0.7" d="M1107 175C1080 158 1062 127 1057 83C1091 96 1113 125 1107 175Z" />
          <path opacity="0.8" d="M1128 153C1116 120 1134 80 1168 60C1171 103 1154 134 1128 153Z" />
          <path opacity="0.7" d="M1099 240C1069 228 1048 199 1033 160C1069 167 1097 193 1099 240Z" />
          <path opacity="0.8" d="M1129 220C1130 186 1148 158 1178 140C1174 177 1158 204 1129 220Z" />
        </g>
      </g>
      <g data-layer="reformer-rear">
        <g data-part="rear-feet" fill={c.dark} opacity="0.9">
          <path d="M587 335H611L599 363H579Z" />
          <path d="M1127 335H1149L1158 363H1138Z" />
        </g>
        <path data-part="rear-footbar" d="M1090 315L1137 244H1168" fill="none" stroke={c.dark} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
        <path data-part="rear-rail" fill={c.soft} d="M581 304H1150L1170 313H559Z" />
        <path data-part="rear-rail-inset" fill={c.primary} opacity="0.65" d="M590 306H1147L1159 310H579Z" />
        <g data-part="pulley-posts" fill={c.primary}>
          <path d="M568 313V255A5.5 5.5 0 0 1 579 255V313Z" />
          <path d="M587 313V269A5 5 0 0 1 597 269V313Z" />
        </g>
        <g data-part="ropes" fill="none" stroke={c.dark} strokeWidth="1.9" strokeLinecap="round">
          <path d="M594 268L683 241" />
          <path d="M594 271L747 304" />
        </g>
        <g data-part="upper-pulley">
          <circle cx="592.5" cy="268.5" r="8" fill={c.dark} />
          <circle cx="592.5" cy="268.5" r="5.5" fill={c.primary} />
        </g>
        <g data-part="carriage">
          <path fill={c.dark} d="M766 292H977A13 13 0 0 1 990 305V312H753V305A13 13 0 0 1 766 292Z" />
          <path fill={c.primary} opacity="0.24" d="M766 292H977A13 13 0 0 1 990 305H753A13 13 0 0 1 766 292Z" />
        </g>
      </g>
      <g data-layer="figure">
        <g data-part="leggings" fill={c.dark}>
          <path d="M907 234C932 240 956 253 981 261C1018 273 1052 282 1087 288L1083 302C1048 300 1006 294 970 287L901 276C884 280 874 276 866 268L875 249Z" />
          <path d="M850 248C832 244 811 247 793 255C778 262 770 272 774 282C777 292 790 296 809 296H876L878 281C889 277 900 272 908 266L881 250Z" />
          <path d="M795 267C814 261 840 272 864 277" fill="none" stroke={c.dark} strokeWidth="1.4" opacity="0.55" />
        </g>
        <g data-part="feet" fill={c.skin}>
          <path d="M871 282C880 283 884 277 890 278C895 279 897 284 902 285C910 286 916 290 920 293Q924 297 918 297H871Z" />
          <path d="M1086 286C1098 290 1109 293 1120 296L1131 300C1137 302 1134 308 1129 309L1119 308C1105 305 1094 302 1082 300Z" />
        </g>
        <g data-part="raised-hand" fill={c.skin}>
          <path d="M866 74C858 61 853 49 843 42C837 40 830 40 825 37L815 33Q812 31 815 30L822 31C817 27 811 26 807 22Q804 19 809 20L825 22C833 24 840 27 846 32C858 43 869 54 879 65Z" />
        </g>
        <g data-part="held-handle">
          <path d="M665 247L691 227Q695 225 696 231L701 244Q702 248 698 248Z" fill="none" stroke={c.dark} strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <g data-part="reaching-hand" fill={c.skin}>
          <path d="M730 216L730 230C720 231 711 234 706 239L704 244Q701 247 698 244L695 237C692 237 690 239 688 241Q684 243 684 239C685 236 689 231 693 228C697 224 702 224 708 223L720 219Z" />
        </g>
        <g data-part="modest-athletic-top" fill={c.primary}>
          <path d="M825 153C833 158 842 156 850 150C858 148 866 143 869 137C876 120 879 104 875 89C873 82 869 75 865 69L878 61C886 73 892 82 895 95C899 114 897 141 896 158C896 181 903 204 918 220C926 229 939 237 950 242C935 249 919 261 903 268C886 277 872 277 858 272C845 267 832 258 814 254C828 249 842 243 848 233C856 220 844 204 825 195C807 204 789 213 769 220C753 226 739 230 727 233L721 218C744 208 767 197 788 183Z" />
        </g>
        <g data-part="neck" fill={c.skin}>
          <path d="M824 130L844 131C843 140 846 147 851 151C848 158 837 160 829 157C825 153 827 144 824 140Z" />
        </g>
        <g data-part="hair" fill={c.dark}>
          <circle cx="792" cy="110" r="13" />
          <path d="M796 105C799 91 811 86 825 87C839 87 848 94 851 106L849 130C848 142 837 146 822 144C807 145 797 137 795 125C790 120 791 111 796 105Z" />
        </g>
        <g data-part="face" fill={c.skin}>
          <path d="M829 96C839 96 844 101 849 108C852 113 852 116 856 120L860 124Q862 126 858 128V134C857 138 852 140 847 140L837 141C830 141 825 136 822 133C817 134 812 131 812 128Q812 122 818 123L824 125C828 115 829 106 829 96Z" />
          <path fill={c.dark} opacity="0.09" d="M832 140C837 142 843 142 847 140C847 143 848 146 850 148C843 148 837 145 832 140Z" />
        </g>
      </g>
      <g data-layer="reformer-front">
        <g data-part="lower-pulley">
          <circle cx="747" cy="304.5" r="8" fill={c.dark} />
          <circle cx="747" cy="304.5" r="5.6" fill={c.primary} />
        </g>
        <g data-part="front-frame">
          <path fill={c.soft} d="M565 312H1167Q1174 312 1174 319V369Q1174 372 1171 372H1154Q1151 372 1150 369L1145 343H587L580 369Q579 372 576 372H562Q559 372 559 369V320Q559 312 565 312Z" />
          <path fill={c.primary} opacity="0.38" d="M565 312H1167Q1174 312 1174 319V369Q1174 372 1171 372H1154Q1151 372 1150 369L1145 343H587L580 369Q579 372 576 372H562Q559 372 559 369V320Q559 312 565 312Z" />
          <path d="M569 314H1165" fill="none" stroke={c.soft} strokeWidth="2" opacity="0.62" />
        </g>
        <path data-part="footbar-padding" d="M1136 244H1168" fill="none" stroke={c.dark} strokeWidth="13" strokeLinecap="round" />
        <path data-part="front-footbar" d="M1118 328L1170 244" fill="none" stroke={c.primary} strokeWidth="13" strokeLinecap="round" />
      </g>
    </svg>
  );
}

/*
 * Padel, football, gymnastics and other: white lines at about 30% on the band, from the design
 * artifact (Owner Home Greeting Bands, v20). On a band of 768px and wider they are 200px high on the
 * right (gymnastics 170px, at the bottom) and the text stops before them (`TEXT_ROOM`). On a narrower
 * band they sit behind the text, so they turn into a dark shade: darker than the band, so white text
 * only gains contrast.
 */
const LINE_ART =
  "pointer-events-none absolute right-0 -z-10 w-auto max-w-none @max-3xl:brightness-0 @max-3xl:opacity-60";
const MIDDLE = "inset-y-0 my-auto h-[200px]";

/**
 * A padel court from above, tilted: glass back walls, the net, the service boxes, one ball. The
 * viewBox holds the whole rotated court with its walls (x 99 to 497, y -3 to 255), about 308px wide
 * at 200px high (review F1.1).
 */
export function PadelArt() {
  return (
    <svg aria-hidden="true" focusable="false" data-art="padel" viewBox="98 -4 400 260" preserveAspectRatio="xMaxYMid meet" className={`${LINE_ART} ${MIDDLE}`}>
      <g transform="translate(118 36) rotate(-12 180 90)" opacity="0.3">
        <g stroke="#FFFFFF" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5">
          <rect x="0" y="0" width="360" height="180" rx="2" />
          <path d="M55 0 V 180 M305 0 V 180 M55 90 H 305" />
        </g>
        <g stroke="#FFFFFF" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="7" opacity="0.8">
          <path d="M72 0 H 0 V 180 H 72" />
          <path d="M288 0 H 360 V 180 H 288" />
        </g>
        <path d="M180 -6 V 186" stroke="#FFFFFF" strokeWidth="5" />
        <path d="M180 -6 V 186" stroke="#FFFFFF" strokeWidth="1" strokeDasharray="3 3" transform="translate(5 0)" />
      </g>
      <path d="M110 186 C 170 110, 250 70, 330 92" stroke="#FFFFFF" fill="none" strokeLinecap="round" strokeWidth="2" strokeDasharray="1 9" opacity="0.55" />
      {/* The ball, in court yellow: the only colour on these four bands. Quiet when it is a shade. */}
      <g className="@max-3xl:opacity-30">
        <circle cx="342" cy="96" r="10" fill="#D9F24A" />
        <path d="M335 89 C 341 95, 341 99, 336 105 M349 87 C 344 93, 344 99, 349 105" stroke="#9FB52A" strokeWidth="1.6" fill="none" />
      </g>
    </svg>
  );
}

/** Mowing stripes across the band, and the halfway line with the centre circle on the right. */
export function FootballArt() {
  return (
    <>
      {/* Darker stripes, not lighter ones: they run under the text. */}
      <div
        aria-hidden="true"
        data-art="football-stripes"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: "repeating-linear-gradient(90deg, rgba(0,0,0,.08) 0 70px, transparent 70px 140px)" }}
      />
      <svg aria-hidden="true" focusable="false" data-art="football" viewBox="130 0 250 220" preserveAspectRatio="xMaxYMid meet" className={`${LINE_ART} ${MIDDLE}`}>
        <g stroke="#FFFFFF" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" opacity="0.3">
          <path d="M210 -10 V 230" />
          <circle cx="210" cy="110" r="74" />
          <circle cx="210" cy="110" r="4.5" fill="#FFFFFF" />
          <path d="M380 34 H 336 V 186 H 380" />
          <path d="M336 70 A 46 46 0 0 0 336 150" strokeWidth="2.5" />
        </g>
      </svg>
    </>
  );
}

const RIBBON = "M196 172 C 260 130, 300 40, 372 64 C 444 88, 418 172, 360 158 C 300 144, 350 44, 450 44 C 540 44, 560 132, 660 110";

/** A rhythmic ribbon that loops across the band, with the beam under it. */
export function GymnasticsArt() {
  return (
    <svg aria-hidden="true" focusable="false" data-art="gymnastics" viewBox="140 0 500 220" preserveAspectRatio="xMaxYMax meet" className={`${LINE_ART} bottom-0 h-[170px]`}>
      <g opacity="0.32">
        <path d="M150 214 L 196 172" stroke="#FFFFFF" strokeWidth="5" strokeLinecap="round" />
        <path d={RIBBON} stroke="#FFFFFF" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="13" opacity="0.55" />
        <path d={RIBBON} stroke="#FFFFFF" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
        <rect x="430" y="196" width="230" height="10" rx="3" fill="#FFFFFF" opacity="0.7" />
        <path d="M470 206 V 222 M630 206 V 222" stroke="#FFFFFF" strokeWidth="4" />
      </g>
    </svg>
  );
}

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];
const WEEK = [
  [1, 1, 0, 1],
  [0, 1, 1, 0],
  [1, 1, 1, 0],
  [1, 0, 1, 1],
  [0, 1, 0, 1],
  [1, 1, 0, 0],
  [0, 0, 1, 0],
];

/** Any other activity: what every business has, a week of sessions, with today marked. */
export function WeekArt() {
  return (
    <svg aria-hidden="true" focusable="false" data-art="other" viewBox="20 0 300 220" preserveAspectRatio="xMaxYMid meet" className={`${LINE_ART} ${MIDDLE}`}>
      <g opacity="0.32">
        <rect x="103" y="22" width="34" height="176" rx="17" fill="#FFFFFF" opacity="0.22" />
        {DAYS.map((day, i) => (
          <text key={i} x={40 + i * 40} y="48" textAnchor="middle" fill="#FFFFFF" fontSize="13" fontWeight="700" fontFamily="system-ui, sans-serif">
            {day}
          </text>
        ))}
        {WEEK.map((column, i) =>
          column.map((on, row) =>
            on ? (
              <circle key={`${i}-${row}`} cx={40 + i * 40} cy={78 + row * 32} r="8" fill="#FFFFFF" />
            ) : (
              <circle key={`${i}-${row}`} cx={40 + i * 40} cy={78 + row * 32} r="8" fill="none" stroke="#FFFFFF" strokeWidth="2" />
            ),
          ),
        )}
      </g>
    </svg>
  );
}
